import { Component, Input, Output, EventEmitter, ViewEncapsulation, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';

import { ToDos } from 'src/providers/to-dos/to-dos';
import { Util } from 'src/shared/util/util';

import { TidyUiComponentsModule } from 'src/tidy-ui-components/tidy-ui-components.module';
import { SharedModule } from 'src/shared/shared.module';

@Component({
  selector: 'job-checklist-display',
  templateUrl: './job-checklist-display.component.html',
  styleUrls: ['./job-checklist-display.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TidyUiComponentsModule,
    SharedModule
  ]
})
export class JobChecklistDisplayComponent {
  @Input() rooms: any[] = [];
  @Input() jobId: number;
  @Input() toDosProvider: ToDos;

  @Output() optionSaved = new EventEmitter<{ roomIndex: number; toDoIndex: number; option: any; newValue: string }>();

  isArray = Array.isArray;

  editingOption: { roomIndex: number; toDoIndex: number; optionId: number } | null = null;
  editForm: UntypedFormGroup;
  editingAllowedValues: { value: string; viewValue: string; checked?: boolean }[] = [];

  constructor(
    private fb: UntypedFormBuilder,
    private util: Util,
    private cdr: ChangeDetectorRef
  ) {}

  isEditingOption(roomIndex: number, toDoIndex: number, optionId: number): boolean {
    return this.editingOption?.roomIndex === roomIndex &&
           this.editingOption?.toDoIndex === toDoIndex &&
           this.editingOption?.optionId === optionId;
  }

  enterEditMode(roomIndex: number, toDoIndex: number, option: any): void {
    this.editingOption = { roomIndex, toDoIndex, optionId: option.id };

    const currentValue = option.customer_value || option.json_value || option.value || '';

    if (option.name?.includes('Checkbox')) {
      this.editingAllowedValues = (option.allowed_values || []).map((val: any) => {
        const viewValue = val && typeof val === 'object' ? (val.viewValue || val.value) : (val || '');
        const selectedValues = currentValue ? currentValue.split(', ') : [];
        return {
          value: viewValue,
          viewValue,
          checked: selectedValues.includes(viewValue)
        };
      });
      this.editForm = this.fb.group({
        value: [currentValue]
      });
    } else if (option.name === 'Select') {
      this.editingAllowedValues = (option.allowed_values || []).map((val: any) => {
        const viewValue = val && typeof val === 'object' ? (val.viewValue || val.value) : (val || '');
        return {
          value: viewValue,
          viewValue
        };
      });
      this.editForm = this.fb.group({
        value: [currentValue]
      });
    } else {
      this.editForm = this.fb.group({
        value: [currentValue]
      });
    }
  }

  cancelEdit(): void {
    this.editingOption = null;
    this.editingAllowedValues = [];
  }

  onCheckboxChange(): void {
    const selectedValues = this.editingAllowedValues
      .filter(item => item.checked)
      .map(item => item.viewValue)
      .join(', ');
    this.editForm.patchValue({ value: selectedValues });
  }

  async saveOptionEdit(roomIndex: number, toDoIndex: number, option: any, toDo: any): Promise<void> {
    try {
      const newValue = this.editForm.get('value')?.value || '';

      const payload = {
        to_dos: [{
          task_id: toDo.id,
          performance: toDo.customer_performance || toDo.performance,
          job_task_options: [{ id: option.id, value: newValue }]
        }]
      };

      await this.toDosProvider.updateInputedToDos(payload, this.jobId);

      this.rooms[roomIndex].todos[toDoIndex].options = this.rooms[roomIndex].todos[toDoIndex].options.map((opt: any) => {
        if (opt.id === option.id) {
          return { ...opt, customer_value: newValue };
        }
        return opt;
      });

      this.cdr.markForCheck();
      this.optionSaved.emit({ roomIndex, toDoIndex, option, newValue });
      this.cancelEdit();
      this.util.showSuccess('Saved');
    } catch (err) {
      this.util.showError('Error saving. Please try again.');
    }
  }

  async toggleTaskPerformance(roomIndex: number, toDoIndex: number, toDo: any): Promise<void> {
    try {
      const currentPerformance = toDo.customer_performance || toDo.performance;
      let newPerformance: string | null;

      // Cycle: performed → not_performed → null (not marked) → performed
      if (currentPerformance === 'performed') {
        newPerformance = 'not_performed';
      } else if (currentPerformance === 'not_performed') {
        newPerformance = null;
      } else {
        newPerformance = 'performed';
      }

      const payload = {
        to_dos: [{
          task_id: toDo.id,
          performance: newPerformance
        }]
      };

      await this.toDosProvider.updateInputedToDos(payload, this.jobId);

      this.rooms[roomIndex].todos[toDoIndex].customer_performance = newPerformance;
      this.cdr.markForCheck();
      this.util.showSuccess('Saved');
    } catch (err) {
      this.util.showError('Error saving. Please try again.');
    }
  }
}
